/**
 * Labeled checkbox aligned with other form fields.
 */

import * as React from 'react';
import { Checkbox as ShadcnCheckbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/** Props for {@link Checkbox}. */
interface CheckboxProps {
  label?: string;
  checked?: boolean | 'indeterminate';
  onCheckedChange?: (checked: boolean) => void;
  onBlur?: () => void;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  state?: 'default' | 'error' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
}

/** Labeled checkbox with the same field states as other form fields. */
export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onCheckedChange,
  onBlur,
  error,
  helperText,
  required,
  disabled,
  className,
  id,
  state,
  size = 'md',
}) => {
  const checkboxId = id || `checkbox-${label?.toLowerCase().replaceAll(/\s+/g, '-')}`;

  const boxSizeClassMap: Record<NonNullable<typeof size>, string> = {
    sm: 'h-4 w-4',
    md: '',
    lg: 'h-6 w-6',
  };
  const boxSizeClass = boxSizeClassMap[size];
  const labelTextClass = size === 'sm' ? 'text-sm' : 'text-base';
  const gapClass = size === 'sm' ? 'space-x-2' : 'space-x-3';

  return (
    <div className={cn('space-y-2', className)}>
      <div className={cn('flex items-center', gapClass)}>
        <ShadcnCheckbox
          id={checkboxId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          className={cn(
            boxSizeClass,
            error && 'border-destructive',
            state === 'success' && 'border-[rgb(10, 134, 66)]',
            state === 'warning' && 'border-[rgb(var(--color-warning))]',
          )}
        />
        {label && (
          <Label
            htmlFor={checkboxId}
            className={cn(
              'form-label leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer',
              labelTextClass,
              required && 'form-label-required',
            )}
          >
            {label}
          </Label>
        )}
      </div>
      {error && <p className="form-error-text">{error}</p>}
      {helperText && !error && <p className="form-helper-text">{helperText}</p>}
    </div>
  );
};
